import { useState } from 'react';
import styled from 'styled-components';
import { spacing, textColors } from 'ui/theme';
import { useDebouncedEffect } from 'utils/hooks/useDebouncedEffect';
import { PushContentDefinition } from 'modules/graphql/types';
import { FormField } from 'ui/formField';
import { Radio } from 'ui/radio';
import { Tooltip } from 'ui/tooltip';
import { TemplateEditViews } from '../../common/types';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';
import { SettingsReadOnlyText } from '../../common/EditorContentContainer.styled';
import { DefaultActionInput } from '../DefaultActionInput';
import { DefaultActionReadOnly } from '../DefaultActionReadOnly';

const StyledReadOnlyText = styled(SettingsReadOnlyText)`
  overflow-wrap: anywhere;
`;

const Label = styled.span`
  color: ${textColors.textPrimary};
  margin-left: ${spacing.quarter};
  padding: ${spacing.quarter} ${spacing.half};
`;

const RadioContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${spacing.half};
`;

const RadioWrapper = styled.div`
  display: flex;
`;

const { openApp, openUrl, openAppLabel, openUrlLabel, customActionLabel, helpText } =
  SETTINGS_DISPLAY_CONTENT.openAction;

export const OpenAction = () => {
  const {
    isEditMode: isEditModeContext,
    liveTemplate,
    setCurrentMediumField,
  } = useEditableFieldsContext();

  const pushData = (liveTemplate?.mediumData || {}) as PushContentDefinition;
  const { view } = useQueryParamContext();
  const [url, setUrl] = useState(pushData.defaultAction?.data || '');
  const [customUrl, setCustomUrl] = useState(pushData.defaultAction?.type || '');
  const isCustomAction =
    pushData.defaultAction?.type !== openUrl && pushData.defaultAction?.type !== openApp;

  // TODO: once MissingUrl is implemented add content error

  useDebouncedEffect(
    () => {
      const updatedAction = { ...pushData.defaultAction };
      const updatedField = !isCustomAction ? 'data' : 'type';
      const updatedUrl = !isCustomAction ? url : customUrl;
      updatedAction[updatedField] = updatedUrl;
      setCurrentMediumField('defaultAction', updatedAction);
    },
    [url, customUrl, isCustomAction],
    {
      skipEffectOnMount: true,
      debounceTimeout: 500,
    },
  );

  const handleRadioClick = (type: string) => {
    setUrl('');

    const defaultAction = {
      type,
      data: type === openUrl ? url : '',
    };

    setCurrentMediumField('defaultAction', defaultAction);
  };

  const handleCustomRadioClick = () => {
    setCustomUrl('');

    const defaultAction = {
      type: customUrl,
    };

    setCurrentMediumField('defaultAction', defaultAction);
  };

  const isEditMode = view === TemplateEditViews.preview || isEditModeContext;

  const getReadOnlyText = () => {
    if (pushData.isSilentPush) return SETTINGS_DISPLAY_CONTENT.deliverSilently.silenced;
    if (pushData.defaultAction?.type)
      return <DefaultActionReadOnly action={pushData.defaultAction} />;
    return SETTINGS_DISPLAY_CONTENT.unsetText;
  };

  return (
    <FormField
      isDisabled={pushData.isSilentPush}
      isHorizontalLayout
      labelText={SETTINGS_DISPLAY_CONTENT.openAction.labelText}
    >
      {isEditMode ? (
        <Tooltip
          isEnabled={pushData.isSilentPush}
          tooltipContent={SETTINGS_DISPLAY_CONTENT.deliverSilently.restriction}
        >
          <RadioContainer>
            <RadioWrapper>
              <Radio
                id="radio-btn-default-action-open-app"
                isDisabled={pushData.isSilentPush}
                isChecked={pushData.defaultAction?.type === openApp}
                onClick={() => handleRadioClick(openApp)}
                value={openApp}
              />
              <Label>{openAppLabel}</Label>
            </RadioWrapper>
          </RadioContainer>

          <RadioContainer>
            <RadioWrapper>
              <Radio
                id="radio-btn-default-action-open-url"
                isDisabled={pushData.isSilentPush}
                isChecked={pushData.defaultAction?.type === openUrl}
                onClick={() => handleRadioClick(openUrl)}
                value={openUrl}
              />
              <Label>{openUrlLabel}</Label>
            </RadioWrapper>

            {pushData.defaultAction?.type === openUrl && (
              <DefaultActionInput
                id="settings-template-push-open-url"
                placeholder={SETTINGS_DISPLAY_CONTENT.openAction.placeholder}
                onChange={(val) => setUrl(val)}
                isDisabled={pushData.defaultAction?.type !== openUrl && pushData.isSilentPush}
                value={url || ''}
                helpText={helpText.url}
              />
            )}
          </RadioContainer>

          <RadioContainer>
            <RadioWrapper>
              <Radio
                id="radio-btn-default-action-custom"
                isDisabled={pushData.isSilentPush}
                isChecked={isCustomAction}
                onClick={handleCustomRadioClick}
                value={customUrl}
              />
              <Label>{customActionLabel}</Label>
            </RadioWrapper>

            {isCustomAction && (
              <DefaultActionInput
                id="settings-template-push-custom-action"
                placeholder={SETTINGS_DISPLAY_CONTENT.openAction.placeholder}
                onChange={(val) => setCustomUrl(val)}
                isDisabled={!isCustomAction || pushData.isSilentPush}
                value={pushData.isSilentPush ? 'Silenced' : customUrl || ''}
                helpText={helpText.custom}
              />
            )}
          </RadioContainer>
        </Tooltip>
      ) : (
        <StyledReadOnlyText isUnset={!pushData.defaultAction} data-test="open-action-ro">
          {getReadOnlyText()}
        </StyledReadOnlyText>
      )}
    </FormField>
  );
};
